"use client";

import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "@nyntax/design-system/lib/utils";
import type * as React from "react";

type InputSize = "xs" | "sm" | "default" | "md" | "lg" | "xl";

// Size reference mirrors `selectTriggerVariants` in `select.tsx`. The existing
// keys (sm/default/lg) keep their original responsive heights; the new keys
// (xs/md/xl) bring over Select's `min-h`/padding scale for visual parity.
const inputSizeClasses: Record<InputSize, string> = {
  xs: "h-6 px-[calc(--spacing(2)-1px)] text-xs leading-6",
  sm: "h-7.5 px-[calc(--spacing(2.5)-1px)] leading-7.5 sm:h-6.5 sm:leading-6.5",
  default:
    "h-8.5 px-[calc(--spacing(3)-1px)] leading-8.5 sm:h-7.5 sm:leading-7.5",
  md: "h-8 px-[calc(--spacing(3)-1px)] leading-8",
  lg: "h-9.5 px-[calc(--spacing(3)-1px)] leading-9.5 sm:h-8.5 sm:leading-8.5",
  xl: "h-10 px-[calc(--spacing(4)-1px)] leading-10",
};

type InputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> & {
  size?: InputSize | number;
  unstyled?: boolean;
  nativeInput?: boolean;
};

function Input({
  className,
  size = "xl",
  unstyled = false,
  nativeInput = false,
  ...props
}: InputProps) {
  const isReadOnly = props.readOnly;

  const sizeClass =
    inputSizeClasses[typeof size === "number" ? "default" : size];

  const inputClassName = cn(
    !isReadOnly &&
      "placeholder:text-muted-foreground/72 w-full min-w-0 rounded-[inherit] outline-none [transition:background-color_5000000s_ease-in-out_0s]",
    !isReadOnly && sizeClass,
    isReadOnly && "w-full min-w-0 cursor-default bg-transparent outline-none",
    props.type === "search" &&
      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
    props.type === "file" &&
      "text-muted-foreground file:text-foreground file:me-3 file:bg-transparent file:text-sm file:font-medium",
    // InputGroup sets control to `contents` — put custom classes on the
    // real input so number/currency styles (tabular-nums, text-end, etc.) land.
    unstyled && className
  );

  return (
    <span
      className={
        cn(
          !unstyled &&
            !isReadOnly &&
            "border-input bg-background text-foreground ring-primary/24 has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-primary has-autofill:bg-foreground/4 dark:bg-input/32 dark:has-autofill:bg-foreground/8 dark:has-aria-invalid:ring-destructive/24 relative inline-flex w-full rounded-lg border text-base shadow-xs/5 transition-shadow not-dark:bg-clip-padding before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:ring-[3px] has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none sm:text-sm dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]",
          !unstyled &&
            isReadOnly &&
            "text-foreground inline-flex w-full text-base sm:text-sm",
          !unstyled && className
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
    >
      {nativeInput ? (
        <input
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          {...(props as React.ComponentProps<"input">)}
        />
      ) : (
        <InputPrimitive
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          {...props}
        />
      )}
    </span>
  );
}

export { Input, type InputProps };
